/**
 * Token-bound monograph table primitives.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useRef } from "react";
import { flexRender, getCoreRowModel, type Row, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "../styling/index.js";
import type {
  MonographDataTableProps,
  MonographPanelTokens,
} from "./MonographPanelPrimitiveTypes.js";

interface TableFactoryInput {
  readonly tokens: MonographPanelTokens;
}

interface VirtualBodyProps<TData> {
  readonly rows: readonly Row<TData>[];
  readonly columnCount: number;
  readonly estimateRowHeight: number;
  readonly scrollRef: React.RefObject<HTMLDivElement | null>;
  readonly renderCells: (row: Row<TData>, index: number) => React.JSX.Element;
}

function cssSize(value: number | string | undefined): string | undefined {
  if (typeof value === "number") return `${value}px`;
  return value;
}

function VirtualBody<TData>({
  rows,
  columnCount,
  estimateRowHeight,
  scrollRef,
  renderCells,
}: VirtualBodyProps<TData>): React.JSX.Element {
  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => estimateRowHeight,
    getScrollElement: () => scrollRef.current,
    overscan: 8,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0) : 0;

  return (
    <tbody data-csk-monograph-table-body="virtualized">
      {paddingTop > 0 ? (
        <tr aria-hidden="true">
          <td colSpan={columnCount} style={{ border: 0, height: paddingTop, padding: 0 }} />
        </tr>
      ) : null}
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (row === undefined) return null;
        return (
          <tr
            key={row.id}
            ref={(node): void => {
              if (node !== null) virtualizer.measureElement(node);
            }}
          >
            {renderCells(row, virtualRow.index)}
          </tr>
        );
      })}
      {paddingBottom > 0 ? (
        <tr aria-hidden="true">
          <td colSpan={columnCount} style={{ border: 0, height: paddingBottom, padding: 0 }} />
        </tr>
      ) : null}
    </tbody>
  );
}

export function createMonographPanelTables({ tokens }: TableFactoryInput): {
  readonly DataTable: <TData>(props: MonographDataTableProps<TData>) => React.JSX.Element;
} {
  function DataTable<TData>({
    data,
    columns,
    ariaLabel,
    getRowId,
    compact = false,
    empty,
    estimateRowHeight = compact ? 42 : 52,
    maxHeight = 460,
    minWidth,
    virtualizeThreshold = 80,
    className,
  }: MonographDataTableProps<TData>): React.JSX.Element {
    const scrollRef = useRef<HTMLDivElement>(null);
    const tableColumns = useMemo(() => [...columns], [columns]);
    const tableData = useMemo(() => [...data], [data]);
    const table = useReactTable({
      columns: tableColumns,
      data: tableData,
      getCoreRowModel: getCoreRowModel(),
      ...(getRowId !== undefined ? { getRowId } : {}),
    });
    const rows = table.getRowModel().rows;
    const leafColumns = table.getAllLeafColumns();
    const shouldVirtualize = rows.length >= virtualizeThreshold;

    const renderCells = (row: Row<TData>, rowIndex: number): React.JSX.Element => (
      <>
        {row.getVisibleCells().map((cell) => (
          <td
            key={cell.id}
            style={{
              background: rowIndex % 2 === 0 ? "rgba(7,6,13,0.30)" : "rgba(7,6,13,0.15)",
              borderTop: `1px solid ${tokens.border}`,
              color: tokens.text,
              fontSize: compact ? 12 : 12.5,
              lineHeight: 1.55,
              padding: compact ? "8px 10px" : "10px 12px",
              verticalAlign: "top",
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </>
    );

    return (
      <div
        ref={scrollRef}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        className={cn("csk-monograph-data-table", className)}
        data-csk-monograph-table="true"
        data-csk-monograph-table-virtualized={shouldVirtualize ? "true" : "false"}
        style={{
          border: `1px solid ${tokens.border}`,
          borderRadius: 12,
          maxHeight: shouldVirtualize ? cssSize(maxHeight) : undefined,
          overflow: "auto",
        }}
      >
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: cssSize(minWidth),
            width: "100%",
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    style={{
                      background: "rgba(4,6,15,0.70)",
                      color: tokens.muted,
                      fontFamily: tokens.mono,
                      fontSize: compact ? 10.5 : 11,
                      fontWeight: 800,
                      letterSpacing: 0,
                      padding: compact ? "8px 10px" : "10px 12px",
                      position: shouldVirtualize ? "sticky" : undefined,
                      textAlign: "left",
                      textTransform: "uppercase",
                      top: shouldVirtualize ? 0 : undefined,
                      verticalAlign: "bottom",
                      zIndex: shouldVirtualize ? 1 : undefined,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {rows.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={Math.max(leafColumns.length, 1)}
                  style={{
                    borderTop: `1px solid ${tokens.border}`,
                    color: tokens.muted,
                    fontSize: 13,
                    padding: "18px 14px",
                  }}
                >
                  {empty ?? "No rows match the current filters."}
                </td>
              </tr>
            </tbody>
          ) : shouldVirtualize ? (
            <VirtualBody
              rows={rows}
              columnCount={Math.max(leafColumns.length, 1)}
              estimateRowHeight={estimateRowHeight}
              scrollRef={scrollRef}
              renderCells={renderCells}
            />
          ) : (
            <tbody data-csk-monograph-table-body="static">
              {rows.map((row, rowIndex) => (
                <tr key={row.id}>{renderCells(row, rowIndex)}</tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    );
  }

  return { DataTable };
}
